import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DiscrepancyBubblesCell } from "components/DiscrepancyBubblesCell";
import { StatusChip } from "components/StatusChip";
import { type MouseEvent } from "react";

import { DiscrepancyTallyView } from "./sections/discrepancyBreakdown";

export interface DiscrepanciesCellProps {
  /** Open-discrepancy counts split by kind (compliance vs field). */
  tally: DiscrepancyTallyView;
  /** When provided, clicking the cell opens the detail modal (and stops the grid row click). */
  onClick?: () => void;
  /**
   * Customer view: show open EPA correction requests plus PENDING + RESOLVED
   * field discrepancies (work in progress + done). Raw unresolved field
   * discrepancies and the missing-e-Manifest compliance bubble stay hidden as
   * internal staff review state. Renders nothing when there's nothing to show.
   * Staff keep the full unresolved/pending/resolved + compliance view.
   */
  customerView?: boolean;
}

/** Tooltip body: a bold header plus a per-line breakdown. */
function BubbleBreakdown({ title, lines }: { title: string; lines: [string, number][] }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        {title}
      </Typography>
      {lines.map(([label, value]) => (
        <Typography key={label} variant="caption" component="div">
          {label}: {value}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Two discrepancy "bubbles": compliance issues (missing e-Manifest + EPA
 * correction requests, open-only) and field discrepancies (cross-source value
 * disagreements). The field bucket uses the shared {@link DiscrepancyBubblesCell}
 * so it renders the same unresolved / pending / resolved breakdown as the staff
 * review grid (ENG-15854) — the two surfaces previously diverged in count and
 * color (the field bubble used to be a single green "open total" chip). The cell
 * renders nothing when the manifest/line has no discrepancies at all. Shared by
 * the manifest list view and the line-item table.
 */
export function DiscrepanciesCell({ tally, onClick, customerView }: DiscrepanciesCellProps) {
  const {
    complianceTotal,
    missingEManifest,
    epaCorrection,
    fieldTotal,
    fieldUnresolved,
    fieldPending,
    fieldResolved,
  } = tally;

  const clickProps = {
    onClick: onClick
      ? (event: MouseEvent) => {
          event.stopPropagation();
          onClick();
        }
      : undefined,
    sx: onClick ? { cursor: "pointer" } : undefined,
  };

  // Customer view: open EPA correction requests (regulator-flagged) plus pending
  // (in progress) + resolved (done) field discrepancies. No raw unresolved FIELD
  // and no missing-e-Manifest bubble — those stay internal to staff review.
  if (customerView) {
    if (epaCorrection === 0 && fieldPending === 0 && fieldResolved === 0) {
      return null;
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" {...clickProps}>
        {epaCorrection > 0 && (
          <Tooltip
            placement="top"
            enterDelay={400}
            title={`${epaCorrection} open EPA correction${epaCorrection === 1 ? "" : "s"}`}
          >
            <StatusChip label={epaCorrection} color="error" />
          </Tooltip>
        )}
        {fieldPending > 0 && (
          <Tooltip placement="top" enterDelay={400} title={`${fieldPending} pending`}>
            <StatusChip label={fieldPending} color="info" />
          </Tooltip>
        )}
        {fieldResolved > 0 && (
          <Tooltip placement="top" enterDelay={400} title={`${fieldResolved} resolved`}>
            <StatusChip label={fieldResolved} color="success" />
          </Tooltip>
        )}
      </Stack>
    );
  }

  if (complianceTotal === 0 && fieldTotal === 0 && fieldResolved === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" {...clickProps}>
      {complianceTotal > 0 && (
        <Tooltip
          placement="top"
          enterDelay={400}
          title={
            <BubbleBreakdown
              title="EPA-flagged"
              lines={[
                ["Missing e-Manifest", missingEManifest],
                ["EPA correction requests", epaCorrection],
                ["Total", complianceTotal],
              ]}
            />
          }
        >
          <StatusChip label={complianceTotal} color="error" />
        </Tooltip>
      )}
      <DiscrepancyBubblesCell
        unresolved={fieldUnresolved}
        pending={fieldPending}
        resolved={fieldResolved}
        unresolvedColor="warning"
        pendingColor="info"
      />
    </Stack>
  );
}
